"use client"

import { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from "react"
import { io, Socket } from 'socket.io-client'
import { useAuth } from "@/hooks/useAuth"

interface ChatMessage {
  id: string
  content: string
  type: 'TEXT' | 'FILE' | 'IMAGE' | 'SYSTEM'
  fileName?: string
  fileUrl?: string
  fileSize?: number
  fileType?: string
  isDeleted?: boolean
  senderId: string
  roomId: string
  replyToId?: string
  createdAt: string
  sender: {
    id: string
    username: string
    avatarUrl?: string | null
  }
  replyTo?: {
    id: string
    content: string
    sender: {
      username: string
      avatarUrl?: string | null
    }
  }
  readBy?: Array<{
    id: string
    userId: string
    username?: string
    avatarUrl?: string | null
  }>
}

interface ChatRoom {
  id: string
  name?: string
  type: 'DIRECT' | 'GROUP' | 'PROJECT'
  isPrivate: boolean
  createdAt: string
  updatedAt: string
  members: Array<{
    id: string
    userId: string
    role: 'ADMIN' | 'MEMBER'
    user: {
      id: string
      username: string
      isOnline: boolean
      lastOnline?: string // Changed from string | undefined to handle null properly
      avatarUrl?: string | null
    }
  }>
  messages: ChatMessage[]
  client?: {
    id: string
    name: string
  }
  _count: {
    messages: number
  }
  imageUrl?: string
  imageName?: string
  imageSize?: number
}

interface User {
  id: string
  username: string
  isOnline: boolean
  lastOnline?: string
  role: 'USER' | 'ADMIN'
  avatarUrl?: string | null
}

interface ChatContextType {
  socket: Socket | null
  isConnected: boolean
  activeRoomId: string | null
  setActiveRoomId: (roomId: string | null) => void
  onlineUsers: Set<string>
  afkUsers: Set<string>
  rooms: ChatRoom[]
  setRooms: (rooms: ChatRoom[]) => void
  messages: ChatMessage[]
  setMessages: (messages: ChatMessage[]) => void
  typingUsers: Map<string, { userId: string; username: string }>
  isTyping: boolean
  setIsTyping: (typing: boolean) => void
  users: User[]
  setUsers: (users: User[]) => void
  sendMessage: (content: string, type?: string, replyToId?: string) => Promise<void>
  sendFile: (file: File, replyToId?: string) => Promise<void>
  deleteMessage: (messageId: string) => Promise<void>
  createDirectRoom: (targetUserId: string) => Promise<void>
  createGroupRoom: (name: string, memberIds: string[], clientId?: string, imageUrl?: string, imageName?: string, imageSize?: number) => Promise<void>
  markAsRead: (messageId: string) => Promise<void>
  startTyping: () => void
  stopTyping: () => void
  joinRoom: (roomId: string) => void
  leaveRoom: (roomId: string) => void
  fetchRoomDetails: (roomId: string) => Promise<void>
  loadChatData: () => Promise<void> // Nowa funkcja do ładowania danych
  isDataLoaded: boolean // Flaga czy dane są załadowane
}

const ChatContext = createContext<ChatContextType | null>(null)

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const { data: session, status } = useAuth() // Używamy naszego useAuth zamiast useSession
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null)
  const [onlineUsers, setOnlineUsers] = useState(new Set<string>())
  const [afkUsers, setAfkUsers] = useState(new Set<string>())
  const [rooms, setRooms] = useState<ChatRoom[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [typingUsers, setTypingUsers] = useState(new Map<string, { userId: string; username: string }>())
  const [isTyping, setIsTyping] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [isDataLoaded, setIsDataLoaded] = useState(false) // Flaga ładowania danych
  const activeRoomIdRef = useRef<string | null>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const defaultTitleRef = useRef<string | null>(null)

  // Load minimal room data (for notifications) - called automatically on socket connect
  const fetchRoomsMinimal = useCallback(async () => {
    try {
      const response = await fetch("/api/chat/rooms?minimal=true")
      if (response.ok) {
        const data = await response.json()
        // Set minimal room data - just IDs, names, and unread counts
        const normalized = data.map((r: any) => ({
          id: r.id,
          name: r.name || '',
          type: r.type,
          imageUrl: r.imageUrl || undefined,
          members: [], // Empty for now
          messages: [], // Empty for now
          _count: r._count || { messages: 0 },
          isPrivate: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        }))
        setRooms(normalized)
      }
    } catch (error) {
      console.error("Error fetching minimal rooms:", error)
    }
  }, [])

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!session?.user?.id) return

    const socketUrl = process.env.NEXT_PUBLIC_SOCKET_URL || 'http://localhost:3001'

    // Try to connect but don't crash if it fails
    let newSocket: Socket | null = null

    try {
      newSocket = io(socketUrl, {
        path: '/chat/socket.io/',
        forceNew: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        timeout: 20000,
        transports: ['websocket', 'polling'],
        autoConnect: true
      })

      newSocket.on('connect', () => {
        console.log('🟢 Connected to Socket.IO server')
        setIsConnected(true)
        newSocket?.emit('user:join', session.user.id)

        // Automatically load minimal room data for notifications
        fetchRoomsMinimal()

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = null
        }
      })

      newSocket.on('disconnect', (reason) => {
        console.log('🔴 Disconnected from Socket.IO server, reason:', reason)
        setIsConnected(false)

        if (reason === 'transport close' || reason === 'ping timeout') {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (newSocket && !newSocket.connected) {
              console.log('🔄 Attempting manual reconnection...')
              try {
                newSocket.connect()
              } catch (err) {
                console.warn('⚠️ Reconnection failed, app will work in offline mode')
              }
            }
          }, 2000)
        }
      })

      newSocket.on('connect_error', (error) => {
        console.warn('⚠️ Socket connection error - app will work in offline mode:', error.message)
        setIsConnected(false)
        // Don't throw error, just log it
      })

      newSocket.on('reconnect', (attemptNumber) => {
        console.log(`🔄 Reconnected after ${attemptNumber} attempts`)
        setIsConnected(true)
        newSocket?.emit('user:join', session.user.id)
      })

      newSocket.on('reconnect_error', (error) => {
        console.warn('⚠️ Reconnection error - app will continue in offline mode:', error.message)
      })

      newSocket.on('reconnect_failed', () => {
        console.warn('⚠️ Socket reconnection failed - app will work in offline mode')
        setIsConnected(false)
      })

      // Handle new messages
      newSocket.on('message:new', (message: ChatMessage) => {
        console.log('📨 New message received:', message, 'readBy:', message.readBy)
        setMessages(prev => [...prev, message])

        setRooms(prev => prev.map(room =>
          room.id === message.roomId
            ? {
              ...room,
              messages: [message],
              updatedAt: message.createdAt,
              _count: {
                ...room._count,
                messages: message.senderId !== session.user.id ? (room._count?.messages || 0) + 1 : (room._count?.messages || 0)
              }
            }
            : room
        ).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()))
      })

      // Handle message read receipts
      newSocket.on('message:read', ({ messageId, userId, roomId }: { messageId: string, userId: string, roomId: string }) => {
        console.log('✅ Message read receipt:', messageId, 'by user:', userId, 'in room:', roomId)

        setMessages(prev => prev.map(msg =>
          msg.id === messageId
            ? {
              ...msg,
              readBy: msg.readBy?.some(read => read.userId === userId)
                ? msg.readBy
                : [...(msg.readBy || []), {
                  id: `${messageId}-${userId}`,
                  userId,
                  username: '',
                  avatarUrl: undefined
                }]
            }
            : msg
        ))

        if (userId === session?.user?.id) {
          setRooms(prev => prev.map(room => {
            if (room.id === roomId) {
              return {
                ...room,
                _count: {
                  ...room._count,
                  messages: Math.max(0, (room._count?.messages || 0) - 1)
                }
              }
            }
            return room
          }))
        }
      })

      // Handle message deletion
      newSocket.on('message:deleted', ({ messageId, roomId }: { messageId: string, roomId: string }) => {
        console.log('🗑️ Message deleted:', messageId, 'in room:', roomId)

        setMessages(prev => prev.map(msg =>
          msg.id === messageId
            ? {
              ...msg,
              isDeleted: true,
              content: 'Message deleted',
              type: 'TEXT' as any
            }
            : msg
        ))
      })

      // Handle individual user online status updates
      newSocket.on('user:online', ({ userId, isOnline, isAFK, lastOnline }: { userId: string, isOnline: boolean, isAFK?: boolean, lastOnline?: string }) => {
        console.log(`👤 User ${userId} status update: ${isOnline ? (isAFK ? 'AFK' : 'online') : 'offline'}`)

        setOnlineUsers(prev => {
          const newSet = new Set(prev)
          if (isOnline) {
            newSet.add(userId)
          } else {
            newSet.delete(userId)
          }
          return newSet
        })

        setAfkUsers(prev => {
          const newSet = new Set(prev)
          if (isOnline && isAFK) {
            newSet.add(userId)
          } else {
            newSet.delete(userId)
          }
          return newSet
        })

        // Update user status in rooms - FIX: Handle null lastOnline properly
        setRooms(prev => prev.map(room => ({
          ...room,
          members: room.members.map(member =>
            member.userId === userId
              ? {
                ...member,
                user: {
                  ...member.user,
                  isOnline,
                  lastOnline: lastOnline || member.user.lastOnline // Convert null to undefined
                }
              }
              : member
          )
        })))

        // Update users list
        setUsers(prev => prev.map(user =>
          user.id === userId
            ? { ...user, isOnline, lastOnline: lastOnline || user.lastOnline } // Convert null to undefined
            : user
        ))
      })

      // Handle bulk online users update
      newSocket.on('users:online:bulk', ({ userIds }: { userIds: string[] }) => {
        console.log('👥 Bulk online users update:', userIds.length, 'users')
        setOnlineUsers(new Set(userIds))

        setRooms(prev => prev.map(room => ({
          ...room,
          members: room.members.map(member => ({
            ...member,
            user: {
              ...member.user,
              isOnline: userIds.includes(member.userId),
              lastOnline: userIds.includes(member.userId) ? undefined : member.user.lastOnline
            }
          }))
        })))

        setUsers(prev => prev.map(user => ({
          ...user,
          isOnline: userIds.includes(user.id),
          lastOnline: userIds.includes(user.id) ? undefined : user.lastOnline
        })))
      })

      // Handle typing indicators
      newSocket.on('typing:start', ({ userId, username, roomId }: { userId: string, username: string, roomId?: string }) => {
        // Only show typing indicator if the typing event is for the currently active room
        if (userId !== session.user.id && roomId && roomId === activeRoomIdRef.current) {
          setTypingUsers(prev => {
            const newMap = new Map(prev)
            newMap.set(userId, { userId, username })
            return newMap
          })
        }
      })

      newSocket.on('typing:stop', ({ userId, roomId }: { userId: string, roomId?: string }) => {
        if (!roomId || roomId !== activeRoomIdRef.current) return
        setTypingUsers(prev => {
          const newMap = new Map(prev)
          newMap.delete(userId)
          return newMap
        })
      })

      // Handle new rooms
      newSocket.on('room:new', (room: ChatRoom) => {
        setRooms(prev => [room, ...prev])
        newSocket?.emit('room:join', room.id)
      })

      // Handle errors
      newSocket.on('error', (error: any) => {
        console.warn('⚠️ Socket error:', error)
        if (error?.message) {
          if (error.message !== 'User not found' && error.message !== 'Not authorized to delete this message') {
            console.warn('⚠️ Socket connection error:', error.message)
          }
        }
      })

      setSocket(newSocket)
    } catch (error) {
      console.warn('⚠️ Failed to initialize socket connection - app will work in offline mode:', error)
      setIsConnected(false)
      setSocket(null)
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      if (newSocket) {
        try {
          newSocket.disconnect()
        } catch (error) {
          console.warn('⚠️ Error disconnecting socket:', error)
        }
      }
    }
  }, [session?.user?.id, fetchRoomsMinimal])

  useEffect(() => {
    if (typeof document === 'undefined') return

    if (!defaultTitleRef.current) {
      defaultTitleRef.current = document.title || 'STALFES - System zarządzania'
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return

    const unreadCount = rooms.reduce((acc, room) => acc + (room._count?.messages ?? 0), 0)

    if (unreadCount > 0) {
      document.title = `Masz (${unreadCount}) Nieodczytane wiadomości`
    } else if (defaultTitleRef.current) {
      document.title = defaultTitleRef.current
    }
  }, [rooms])

  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined' && defaultTitleRef.current) {
        document.title = defaultTitleRef.current
      }
    }
  }, [])

  // Usunięty automatyczny load przy połączeniu socket
  // useEffect(() => {
  //   if (session?.user?.id && socket && isConnected) {
  //     fetchRooms()
  //     fetchUsers()
  //   }
  // }, [session?.user?.id, socket, isConnected])

  // Load messages when room changes
  useEffect(() => {
    if (!activeRoomId || !socket) return
    // keep a ref for event handlers
    activeRoomIdRef.current = activeRoomId

    // clear typing users when switching rooms
    setTypingUsers(new Map())

    fetchMessages(activeRoomId)
    socket.emit('room:join', activeRoomId)

    return () => {
      socket.emit('room:leave', activeRoomId)
      activeRoomIdRef.current = null
    }
  }, [activeRoomId, socket])

  // Inactivity AFK Detection: 60 minutes of no user activity -> AFK ("Zaraz wracam", żółta kropka)
  useEffect(() => {
    if (!socket || !isConnected || !session?.user?.id) return

    const userId = session.user.id
    const AFK_TIMEOUT_MS = 60 * 60 * 1000 // 60 minut (1 godzina) braku ruchu -> AFK ("Zaraz wracam")

    let afkTimer: NodeJS.Timeout | null = null
    let isAFK = false

    const sendStatus = (online: boolean, afk: boolean) => {
      if (socket && socket.connected) {
        socket.emit('user:status:update', {
          userId,
          isOnline: online,
          isAFK: afk,
        })
      }
    }

    const markAFK = () => {
      if (!isAFK) {
        isAFK = true
        console.log('🟡 User has been inactive for 1 hour - marking AFK (Zaraz wracam)')
        sendStatus(true, true)
      }
    }

    const resetIdleTimers = () => {
      if (isAFK) {
        isAFK = false
        console.log('⚡ User returned from AFK inactivity - marking ONLINE')
        sendStatus(true, false)
      }

      if (afkTimer) clearTimeout(afkTimer)
      afkTimer = setTimeout(markAFK, AFK_TIMEOUT_MS)
    }

    // Event listeners for user activity (mouse movement, key presses, touch, scroll)
    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll']
    activityEvents.forEach((evt) => {
      window.addEventListener(evt, resetIdleTimers, { passive: true })
    })

    // Handle tab visibility change
    const handleVisibilityChange = () => {
      resetIdleTimers()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Initial timer setup
    afkTimer = setTimeout(markAFK, AFK_TIMEOUT_MS)

    // Periodic heartbeat only if user is actively ONLINE (not AFK)
    const heartbeatInterval = setInterval(() => {
      if (!isAFK) {
        sendStatus(true, false)
      }
    }, 30000)

    return () => {
      if (afkTimer) clearTimeout(afkTimer)
      clearInterval(heartbeatInterval)
      activityEvents.forEach((evt) => {
        window.removeEventListener(evt, resetIdleTimers)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [socket, isConnected, session?.user?.id])

  // Load full room data (with members, messages) - called lazy when chat opens
  const fetchRooms = useCallback(async () => {
    try {
      const response = await fetch("/api/chat/rooms")
      if (response.ok) {
        const data = await response.json()
        // Ensure rooms have members and defaults so UI can render member lists
        const normalized = data.map((r: ChatRoom) => ({
          ...r,
          members: r.members || [],
          messages: r.messages || [],
          _count: r._count || { messages: 0 },
          imageUrl: (r as any).imageUrl || undefined,
          imageName: (r as any).imageName || undefined,
          imageSize: (r as any).imageSize || undefined,
        }))
        setRooms(normalized)

        data.forEach((room: ChatRoom) => {
          socket?.emit('room:join', room.id)
        })
      }
    } catch (error) {
      console.error("Error fetching rooms:", error)
    }
  }, [socket])

  const fetchRoomDetails = useCallback(async (roomId: string) => {
    try {
      const res = await fetch(`/api/chat/rooms/${roomId}`)
      if (res.ok) {
        const room = await res.json()
        setRooms(prev => prev.map(r => r.id === roomId ? { ...r, ...room } : r))
      }
    } catch (error) {
      console.error('Error fetching room details:', error)
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch("/api/chat/users")
      if (response.ok) {
        const data = await response.json()
        setUsers(data)

        if (!isConnected) {
          const onlineUserIds = data.filter((user: User) => user.isOnline).map((user: User) => user.id)
          setOnlineUsers(new Set(onlineUserIds))
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error)
    }
  }, [isConnected])

  // Load initial data - TYLKO gdy jest jawnie wywołane (lazy loading)
  const loadChatData = useCallback(async () => {
    if (isDataLoaded || !session?.user?.id) return

    // Dodaj retry logic jeśli socket nie jest podłączony
    if (!socket || !isConnected) {
      console.log('⏳ Waiting for socket connection before loading chat data...')
      // Spróbuj ponownie za 1 sekundę
      setTimeout(() => {
        if (socket && isConnected) {
          loadChatData()
        }
      }, 1000)
      return
    }

    try {
      console.log('📥 Loading chat data...')
      await Promise.all([
        fetchRooms(),
        fetchUsers()
      ])
      setIsDataLoaded(true)
      console.log('✅ Chat data loaded successfully')
    } catch (error) {
      console.error("❌ Error loading chat data:", error)
      // Spróbuj ponownie za 2 sekundy w przypadku błędu
      setTimeout(() => {
        if (!isDataLoaded) {
          console.log('🔄 Retrying to load chat data...')
          loadChatData()
        }
      }, 2000)
    }
  }, [isDataLoaded, session?.user?.id, socket, isConnected, fetchRooms, fetchUsers])

  const fetchMessages = async (roomId: string) => {
    try {
      const response = await fetch(`/api/chat/rooms/${roomId}/messages`)
      if (response.ok) {
        const data = await response.json()
        console.log('💾 Fetched messages:', data.length, 'messages, first message readBy:', data[0]?.readBy)
        setMessages(data)
      }
    } catch (error) {
      console.error("Error fetching messages:", error)
    }
  }

  const sendMessage = async (content: string, type: string = 'TEXT', replyToId?: string) => {
    if (!activeRoomId || !content.trim() || !session?.user?.id) return

    if (!socket || !isConnected) {
      console.warn('⚠️ Cannot send message - not connected to chat server')
      return
    }

    try {
      socket.emit('message:send', {
        roomId: activeRoomId,
        content: content.trim(),
        type,
        replyToId,
        userId: session.user.id
      })
    } catch (error) {
      console.warn('⚠️ Error sending message:', error)
    }
  }

  const sendFile = async (file: File, replyToId?: string) => {
    if (!activeRoomId || !session?.user?.id) return

    if (!socket || !isConnected) {
      console.warn('⚠️ Cannot send file - not connected to chat server')
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/api/chat/upload', {
        method: 'POST',
        body: formData
      })

      if (response.ok) {
        const fileData = await response.json()

        socket.emit('file:uploaded', {
          roomId: activeRoomId,
          fileData,
          userId: session.user.id,
          replyToId
        })
      }
    } catch (error) {
      console.warn("⚠️ Error uploading file:", error)
    }
  }

  const deleteMessage = async (messageId: string) => {
    if (!session?.user?.id) return

    if (!socket || !isConnected) {
      console.warn('⚠️ Cannot delete message - not connected to chat server')
      return
    }

    try {
      socket.emit('message:delete', {
        messageId,
        userId: session.user.id
      })
    } catch (error) {
      console.warn("⚠️ Error deleting message:", error)
    }
  }

  const createDirectRoom = async (targetUserId: string) => {
    try {
      const response = await fetch('/api/chat/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'DIRECT',
          targetUserId
        })
      })

      if (response.ok) {
        const room = await response.json()
        const roomWithDefaults = {
          ...room,
          messages: room.messages || [],
          _count: room._count || { messages: 0 }
        }
        setRooms(prev => [roomWithDefaults, ...prev])
        setActiveRoomId(room.id)

        if (socket && isConnected) {
          try {
            socket.emit('room:created', room)
          } catch (error) {
            console.warn('⚠️ Error notifying socket about new room:', error)
          }
        }
      }
    } catch (error) {
      console.error("Error creating direct room:", error)
    }
  }

  const createGroupRoom = async (name: string, memberIds: string[], clientId?: string, imageUrl?: string, imageName?: string, imageSize?: number) => {
    try {
      const response = await fetch('/api/chat/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'GROUP',
          name,
          memberIds,
          clientId,
          imageUrl,
          imageName,
          imageSize
        })
      })

      if (response.ok) {
        const room = await response.json()
        const roomWithDefaults = {
          ...room,
          messages: room.messages || [],
          _count: room._count || { messages: 0 }
        }
        setRooms(prev => [roomWithDefaults, ...prev])
        setActiveRoomId(room.id)

        if (socket && isConnected) {
          try {
            socket.emit('room:created', room)
          } catch (error) {
            console.warn('⚠️ Error notifying socket about new room:', error)
          }
        }
      }
    } catch (error) {
      console.error("Error creating group room:", error)
    }
  }

  const markAsRead = async (messageId: string) => {
    if (!session?.user?.id) return

    if (!socket || !isConnected) {
      // Silently fail if not connected
      return
    }

    try {
      socket.emit('message:read', {
        messageId,
        userId: session.user.id
      })
    } catch (error) {
      console.warn("⚠️ Error marking message as read:", error)
    }
  }

  const startTyping = () => {
    if (!activeRoomId || !session?.user?.id) return

    if (!socket || !isConnected) {
      return
    }

    setIsTyping(true)
    try {
      socket.emit('typing:start', {
        roomId: activeRoomId,
        userId: session.user.id,
        username: session.user.username
      })
    } catch (error) {
      console.warn("⚠️ Error sending typing indicator:", error)
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    typingTimeoutRef.current = setTimeout(() => {
      stopTyping()
    }, 3000)
  }

  const stopTyping = () => {
    if (!activeRoomId || !session?.user?.id) return

    setIsTyping(false)

    if (!socket || !isConnected) {
      return
    }

    try {
      socket.emit('typing:stop', {
        roomId: activeRoomId,
        userId: session.user.id
      })
    } catch (error) {
      console.warn("⚠️ Error sending typing stop:", error)
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }
  }

  const joinRoom = (roomId: string) => {
    setActiveRoomId(roomId)
    if (socket && isConnected) {
      try {
        socket.emit('room:join', roomId)
      } catch (error) {
        console.warn('⚠️ Error joining room via socket:', error)
      }
    }
    loadRoomMessages(roomId)
    // ensure we have full room data (members) for the active room
    fetchRoomDetails(roomId)
  }

  const loadRoomMessages = async (roomId: string) => {
    try {
      const response = await fetch(`/api/chat/rooms/${roomId}/messages`)
      if (response.ok) {
        const roomMessages = await response.json()
        setMessages(roomMessages)

        setRooms(prev => prev.map(room =>
          room.id === roomId
            ? { ...room, _count: { ...room._count, messages: 0 } }
            : room
        ))
      }
    } catch (error) {
      console.error('Error loading room messages:', error)
    }
  }

  const leaveRoom = (roomId: string) => {
    if (activeRoomId === roomId) {
      setActiveRoomId(null)
      setMessages([])
      if (socket && isConnected) {
        try {
          socket.emit('room:leave', roomId)
        } catch (error) {
          console.warn('⚠️ Error leaving room via socket:', error)
        }
      }
    }
  }

  return (
    <ChatContext.Provider value={{
      socket,
      isConnected,
      activeRoomId,
      setActiveRoomId,
      onlineUsers,
      afkUsers,
      rooms,
      setRooms,
      messages,
      setMessages,
      typingUsers,
      isTyping,
      setIsTyping,
      users,
      setUsers,
      sendMessage,
      sendFile,
      deleteMessage,
      createDirectRoom,
      createGroupRoom,
      markAsRead,
      startTyping,
      stopTyping,
      joinRoom,
      leaveRoom,
      fetchRoomDetails,
      loadChatData,
      isDataLoaded
    }}>
      {children}
    </ChatContext.Provider>
  )
}

export const useChat = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error("useChat must be used within ChatProvider")
  }
  return context
}